/**
 * Выбор локации для календаря: какой остров сейчас на экране и что из него следует.
 *
 * Один общий календарь на Бали и Пхукет читался неправильно всегда: две площадки в одной сетке
 * стоят в поясах, разведённых на час, и «10:00» в строке значило разное время в соседних
 * карточках. Список инструкторов в ресурсной сетке при этом складывался из обоих островов, и
 * половина колонок была заведомо пустой. Поэтому локация — не фильтр поверх календаря, а его
 * контекст: пояс сетки, границы периода, набор площадок и набор инструкторов.
 *
 * Здесь только чистые правила выбора и отбора; запросы и состояние — в `ManagerCalendar`.
 */

import type { Instructor, Location, ManagerCalendarEvent, Venue } from "../../../api/schemas.js";

/** Выбор менеджера переживает перезагрузку: остров меняют раз в день, а не раз в минуту. */
const STORAGE_KEY = "pwz.calendar.locationId";

export function readStoredLocationId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Приватное окно и заблокированные куки — не повод падать: остров просто выберется заново.
    return null;
  }
}

export function storeLocationId(locationId: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locationId);
  } catch {
    // Не сохранилось — переживём: это удобство, а не состояние приложения.
  }
}

/**
 * Локация, в контексте которой рисуется календарь.
 *
 * В карточке amo выбирать нечего: сделка уже принадлежит острову, и переключатель там показывал
 * бы менеджеру чужие площадки поверх собственной сделки. В общем календаре берётся выбранная,
 * а если выбранной больше нет в каталоге (переименовали, выключили) — первая активная.
 */
export function resolveActiveLocation(input: {
  readonly locations: readonly Location[];
  readonly selectedId: string | null;
  /** Локация сделки в режиме карточки amo; `null` в общем календаре. */
  readonly dealLocationId: string | null;
}): Location | null {
  if (input.dealLocationId !== null) {
    return input.locations.find((location) => location.id === input.dealLocationId) ?? null;
  }
  const selected = input.locations.find((location) => location.id === input.selectedId);
  return selected ?? input.locations[0] ?? null;
}

/** Площадка → её локация. Урок знает площадку, а не остров: связать можно только через каталог. */
export function venueLocationLookup(venues: readonly Venue[]): Map<string, string | null> {
  return new Map(venues.map((venue) => [venue.id, venue.locationId ?? null]));
}

/**
 * Уроки этого острова. Урок без площадки остаётся видимым: он ещё не назначен никуда, и спрятать
 * его значило бы потерять из календаря работу, которую как раз и надо распределить.
 */
export function filterEventsByLocation(
  events: readonly ManagerCalendarEvent[],
  venueLocations: ReadonlyMap<string, string | null>,
  locationId: string | null,
): ManagerCalendarEvent[] {
  if (locationId === null) return [...events];
  return events.filter((event) => {
    if (event.venueId === null) return true;
    const venueLocation = venueLocations.get(event.venueId);
    // Площадки нет в каталоге — не наше дело её прятать: пусть менеджер увидит и разберётся.
    return venueLocation === undefined || venueLocation === locationId;
  });
}

/** Площадки острова; площадка без локации показывается везде, иначе её нельзя было бы выбрать. */
export function filterVenuesByLocation(
  venues: readonly Venue[],
  locationId: string | null,
): Venue[] {
  if (locationId === null) return [...venues];
  return venues.filter((venue) => !venue.locationId || venue.locationId === locationId);
}

/** Инструкторы острова; инструктор без локации в карточке 7539 виден на обоих. */
export function filterInstructorsByLocation(
  instructors: readonly Instructor[],
  locationId: string | null,
): Instructor[] {
  if (locationId === null) return [...instructors];
  return instructors.filter(
    (instructor) => !instructor.locationId || instructor.locationId === locationId,
  );
}
